import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Scan, X, Camera, CreditCard, Usb, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { parseScannedId, normalizeGuestData } from '../utils/idScanner';

const IDScannerModal = ({ open, onClose, onDataScanned }) => {
  const [step, setStep] = useState('select');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [scannedText, setScannedText] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState('');
  const [barcodeScanningAvailable, setBarcodeScanningAvailable] = useState(true);
  const videoRef = useRef(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Cleanup when modal closes
    if (!open) {
      stopCamera();
      setStep('select');
      setSelectedMethod(null);
      setScannedText('');
      setScanning(false);
      setScanStatus('');
    } else {
      // Check if barcode scanning is available when modal opens
      checkBarcodeScanningStatus();
    }
  }, [open]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      stopCamera();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const stopCamera = () => {
    try {
      // Stop video tracks
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
    } catch (err) {
      console.log('[ID Scanner] Camera stop:', err.message);
    }
  };

  const checkBarcodeScanningStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/id-scanning/status`
      );
      
      console.log('[ID Scanner] Status check response:', response.data);
      
      if (response.data && response.data.status === 'operational') {
        setBarcodeScanningAvailable(true);
        console.log('[ID Scanner] ✅ Barcode scanning is available');
      } else {
        setBarcodeScanningAvailable(false);
        console.warn('[ID Scanner] ⚠️ Barcode scanning unavailable:', response.data?.message);
      }
    } catch (error) {
      console.error('[ID Scanner] Failed to check status:', error);
      // Default to available on error (better UX for network issues)
      setBarcodeScanningAvailable(true);
      console.warn('[ID Scanner] Defaulting to available due to status check error');
    }
  };

  const startCamera = async () => {
    setScanStatus('Starting camera...');
    
    try {
      // For cheap webcams: Use photo capture mode instead of continuous scanning
      // This allows users to get barcode in focus before capturing
      
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920, min: 1280 }, // Higher resolution for better barcode reading
          height: { ideal: 1080, min: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      setScanStatus('📸 Position barcode, then click CAPTURE');
      console.log('[ID Scanner] Camera ready - Photo capture mode for better quality');
      
    } catch (err) {
      console.error('[ID Scanner] Camera error:', err);
      setScanStatus(`Camera access denied. Use Mag Stripe Reader instead (instant!).`);
      
      setTimeout(() => {
        setStep('select');
        setSelectedMethod(null);
      }, 3000);
    }
  };

  const captureAndDecode = async () => {
    if (!videoRef.current) return;
    
    setScanStatus('📷 Capturing image...');
    
    try {
      // Create canvas to capture video frame at high quality
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      setScanStatus('☁️ Sending to server for decoding...');
      
      // Convert canvas to blob
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95);
      });
      
      // Create FormData
      const formData = new FormData();
      formData.append('file', blob, 'id-capture.jpg');
      
      // Get auth token
      const token = localStorage.getItem('token');
      
      // Send to backend
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/id-scanning/decode-barcode`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      console.log('[ID Scanner] Backend response:', response.data);
      
      if (response.data.success && response.data.data) {
        const parsedData = response.data.data;
        console.log('[ID Scanner] ✅ Successfully decoded barcode!');
        console.log('[ID Scanner] Name:', parsedData.fullName);
        console.log('[ID Scanner] DOB:', parsedData.dateOfBirth);
        console.log('[ID Scanner] State:', parsedData.state);
        
        // Stop camera
        stopCamera();
        
        // Normalize and send to parent
        const normalized = {
          fullName: parsedData.fullName,
          firstName: parsedData.firstName || '',
          lastName: parsedData.lastName || '',
          documentNumber: parsedData.documentNumber,
          documentType: parsedData.documentType,
          dateOfBirth: parsedData.dateOfBirth,
          addressLine1: parsedData.addressLine1,
          city: parsedData.city,
          state: parsedData.state,
          postalCode: parsedData.postalCode,
          country: parsedData.country
        };
        
        onDataScanned(normalized);
        handleClose();
      } else {
        console.error('[ID Scanner] Decoding failed:', response.data.error);
        setScanStatus(`❌ ${response.data.error || 'Could not read barcode'}`);
        
        setTimeout(() => {
          setScanStatus('📸 Position barcode, then click CAPTURE (or try Mag Stripe)');
        }, 4000);
      }
      
    } catch (err) {
      console.error('[ID Scanner] Error:', err);
      const errorMsg = err.response?.data?.detail || err.message;
      setScanStatus(`❌ Error: ${errorMsg}`);
      
      setTimeout(() => {
        setScanStatus('📸 Position barcode, then click CAPTURE (or try Mag Stripe)');
      }, 4000);
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setStep('scanning');
    setScanning(true);
    setScannedText('');
    setScanStatus('');
    
    if (method === 'camera') {
      // Start ZXing camera scanner
      setTimeout(() => startCamera(), 300);
    } else {
      // Auto-focus for USB/Mag stripe
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setScannedText(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const delay = selectedMethod === 'magstripe' ? 1000 : 800;
    timeoutRef.current = setTimeout(() => {
      processScannedData(value);
    }, delay);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      processScannedData(scannedText);
    }
    
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const processScannedData = (rawData) => {
    if (!rawData || rawData.trim().length < 10) {
      return;
    }

    console.log('[ID Scanner] Processing USB/Mag stripe data');
    const parsed = parseScannedId(rawData);
    
    if (parsed) {
      const normalized = normalizeGuestData(parsed);
      onDataScanned(normalized);
      handleClose();
    } else {
      alert('Failed to parse ID. Please try again or enter manually.');
      setStep('select');
      setSelectedMethod(null);
      setScannedText('');
    }
  };

  const handleBack = () => {
    stopCamera();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setStep('select');
    setSelectedMethod(null);
    setScanning(false);
    setScannedText('');
    setScanStatus('');
  };

  const handleCancel = () => {
    stopCamera();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    onClose();
  };

  const handleClose = () => {
    stopCamera();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setStep('select');
    setSelectedMethod(null);
    setScanning(false);
    setScannedText('');
    setScanStatus('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleClose();
    }}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scan className="text-cyan-600" size={20} />
            Scan ID
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3">
          {/* Step 1: Method Selection */}
          {step === 'select' && (
            <div className="py-2">
              <p className="text-center text-slate-700 font-medium mb-2 text-sm">
                Select Scanning Method
              </p>
              
              {/* Recommendation for elderly users */}
              <div className="mb-3 p-2 bg-cyan-50 border border-cyan-300 rounded">
                <p className="text-xs text-cyan-900 font-bold">⭐ Recommended: Mag Stripe Reader</p>
                <p className="text-xs text-cyan-800">Fastest & easiest - just swipe! Perfect for quick check-ins.</p>
              </div>

              {/* Warning when barcode scanning is unavailable */}
              {!barcodeScanningAvailable && (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-300 rounded">
                  <p className="text-xs text-amber-900 font-bold">⚠️ Camera Barcode Scanning Unavailable</p>
                  <p className="text-xs text-amber-800">Please use Mag Stripe Reader or USB Scanner instead.</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mb-3">
                <button
                  onClick={() => handleMethodSelect('camera')}
                  disabled={!barcodeScanningAvailable}
                  className={`border-2 rounded-lg p-4 transition-all text-center ${
                    barcodeScanningAvailable 
                      ? 'border-slate-200 hover:border-cyan-500 hover:bg-cyan-50' 
                      : 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Camera className={`mx-auto mb-2 ${barcodeScanningAvailable ? 'text-cyan-600' : 'text-slate-400'}`} size={32} />
                  <p className={`font-medium text-sm ${barcodeScanningAvailable ? 'text-slate-900' : 'text-slate-400'}`}>Camera</p>
                  <p className="text-xs text-slate-500">PDF417</p>
                  {!barcodeScanningAvailable && (
                    <p className="text-xs text-red-600 mt-1">Unavailable</p>
                  )}
                </button>

                <button
                  onClick={() => handleMethodSelect('usb')}
                  className="border-2 border-slate-200 rounded-lg p-4 hover:border-cyan-500 hover:bg-cyan-50 transition-all text-center"
                >
                  <Usb className="mx-auto text-cyan-600 mb-2" size={32} />
                  <p className="font-medium text-slate-900 text-sm">USB</p>
                  <p className="text-xs text-slate-500">Scanner</p>
                </button>

                <button
                  onClick={() => handleMethodSelect('magstripe')}
                  className="border-2 border-slate-200 rounded-lg p-4 hover:border-cyan-500 hover:bg-cyan-50 transition-all text-center"
                >
                  <CreditCard className="mx-auto text-cyan-600 mb-2" size={32} />
                  <p className="font-medium text-slate-900 text-sm">Mag Stripe</p>
                  <p className="text-xs text-slate-500">Swipe</p>
                </button>
              </div>

              <p className="text-xs text-center text-slate-500">
                Or close and enter manually
              </p>
            </div>
          )}

          {/* Step 2: Scanning */}
          {step === 'scanning' && (
            <div className="py-2">
              {/* Camera Scanning */}
              {selectedMethod === 'camera' && (
                <>
                  <div className="mb-3">
                    <div className="relative">
                      <video
                        ref={videoRef}
                        className="w-full rounded-lg border-4 border-green-500 bg-black"
                        style={{ maxHeight: '400px' }}
                        autoPlay
                        playsInline
                      />
                      
                      {scanStatus && (
                        <div className="absolute top-2 left-2 right-2 bg-green-600 text-white px-3 py-2 rounded text-sm font-medium text-center shadow-lg">
                          {scanStatus}
                        </div>
                      )}
                      
                      {/* Large CAPTURE button overlay */}
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                        <Button 
                          onClick={captureAndDecode}
                          size="lg"
                          className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-6 text-lg shadow-xl"
                        >
                          📷 CAPTURE BARCODE
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                      <p className="font-bold mb-1">⚡ Smart Backend Processing:</p>
                      <ul className="space-y-0.5">
                        <li>• FLIP ID TO BACK - find rectangular barcode</li>
                        <li>• Position barcode in center of video</li>
                        <li>• Move closer/farther until barcode looks sharp</li>
                        <li>• Click <strong>CAPTURE BARCODE</strong> button</li>
                        <li>• Server decodes with advanced image processing!</li>
                      </ul>
                      <p className="mt-2 text-xs text-amber-700 font-medium">
                        💡 Still difficult? <strong>Mag Stripe</strong> is instant - just swipe!
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => { stopCamera(); onClose(); }} variant="default" size="sm" className="flex-1">
                      Skip - Enter Manually
                    </Button>
                    <Button onClick={handleBack} variant="outline" size="sm" className="flex-1">
                      <ArrowLeft size={14} className="mr-1" />
                      Back
                    </Button>
                  </div>
                </>
              )}

              {/* USB/Mag Stripe Scanning */}
              {selectedMethod !== 'camera' && (
                <>
                  <div className="text-center mb-3">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2 animate-pulse">
                      {selectedMethod === 'usb' ? <Usb className="text-green-600" size={32} /> : <CreditCard className="text-green-600" size={32} />}
                    </div>
                    <p className="text-base font-medium text-slate-900">
                      {selectedMethod === 'usb' ? 'USB Scanner Ready' : 'Mag Stripe Reader Ready'}
                    </p>
                    <p className="text-sm text-green-700 font-medium">
                      {selectedMethod === 'usb' ? '🔌 Place ID and scan' : '💳 Swipe ID now'}
                    </p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
                    <p className="text-xs font-semibold text-green-900 mb-1">Instructions:</p>
                    <ol className="text-xs text-green-800 space-y-1">
                      {selectedMethod === 'usb' ? (
                        <>
                          <li><strong>1.</strong> Place ID face-down on scanner</li>
                          <li><strong>2.</strong> Press scan button</li>
                          <li><strong>3.</strong> Data auto-populates</li>
                        </>
                      ) : (
                        <>
                          <li><strong>1.</strong> Swipe ID through reader</li>
                          <li><strong>2.</strong> One smooth motion</li>
                          <li><strong>3.</strong> Data auto-fills</li>
                        </>
                      )}
                    </ol>
                  </div>

                  <textarea
                    ref={inputRef}
                    value={scannedText}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    className="sr-only"
                    autoFocus
                  />
                  
                  {scannedText && (
                    <div className="mb-3 p-2 bg-slate-100 rounded border text-xs font-mono max-h-20 overflow-auto">
                      {scannedText.substring(0, 150)}...
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={handleBack} variant="outline" size="sm" className="flex-1">
                      <ArrowLeft size={14} className="mr-1" />
                      Back
                    </Button>
                    <Button onClick={handleCancel} variant="outline" size="sm" className="flex-1">
                      <X size={14} className="mr-1" />
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IDScannerModal;
